import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { renderAsync } from "docx-preview";
import "./TranslateFilePreview.css";

import { useConstants } from "../../constant/constantProvider";

// assets
import DOWNLOAD from "../../assets/images/download-document.svg";
import ZOOM_IN from "../../assets/images/zoomIn.svg";
import ZOOM_OUT from "../../assets/images/zoomOut.svg";

// Worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export const TranslateFilePreview = ({
  fileURL,
  fileType,
  fileName,
  enabledActionButton = true,
  isLoading = false,
}) => {
  const docxContainerRef = useRef(null);
  const documentRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);

  const { LOADING_PLEASE_WAIT } = useConstants();

  const isPDF = fileType === "pdf";
  const isDOC = ["doc", "docx"].includes(fileType);
  const isImage = ["jpg", "jpeg", "png", "webp", "image"].includes(fileType);
  const isSupported = isPDF || isDOC || isImage;

  useEffect(() => {
    if (!fileURL || isLoading) return;

    // Reset zoom to 1 when file changes
    setZoomLevel(1);
    setNumPages(null);

    // Small delay to ensure container is rendered and has width
    const timer = setTimeout(() => {
      // Measure width
      if (documentRef.current) {
        setContainerWidth(documentRef.current.offsetWidth - 40);
      }

      if (["doc", "docx"].includes(fileType)) renderDOCX(fileURL);
    }, 100);

    return () => clearTimeout(timer);
  }, [fileURL, fileType, isLoading]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // DOCX Preview
  const renderDOCX = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      if (docxContainerRef.current) {
        docxContainerRef.current.innerHTML = "";
        await renderAsync(blob, docxContainerRef.current, null, {
          className: "docx",
          inWrapper: true,
        });
      }
    } catch (err) {
      console.error("DOCX load error:", err);
    }
  };

  // Download handler
  const handleDownload = async () => {
    try {
      let blob;
      const response = await fetch(fileURL);
      blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "document";
      link.click();

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(5, prev + 0.25));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(1, prev - 0.25));
  };

  return (
    <div className={`file-preview-container ${zoomLevel !== 1 ? 'zoom-active' : ''}`}>
      {enabledActionButton && !isLoading && (
        <div className="document-preview-action-section">
          <button
            className="button-style zoom-out"
            title="Zoom Out"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
          >
            <img src={ZOOM_OUT} alt="zoom out" />
          </button>
          <button
            className="button-style zoom-in"
            title="Zoom In"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 5}
          >
            <img src={ZOOM_IN} alt="zoom in" />
          </button>
          <button
            className="button-style download"
            title="Download"
            onClick={handleDownload}
          >
            <img src={DOWNLOAD} alt="download" />
          </button>
        </div>
      )}

      <div className={`document-preview-section ${zoomLevel !== 1 ? 'zoom-active' : ''}`}>
        {isLoading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{LOADING_PLEASE_WAIT}</p>
          </div>
        )}
        <div
          ref={documentRef}
          className="document"
          style={{ display: isLoading ? "none" : "block" }}
        >
          {isPDF && (
            <div
              className={`pdf-preview ${zoomLevel === 1 ? 'zoom-fit' : ''}`}
              style={{
                transform: zoomLevel === 1 ? 'none' : `scale(${zoomLevel})`,
                transformOrigin: 'top left',
                width: '100%'
              }}
            >
              <Document
                file={fileURL}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div className="loading-spinner"></div>}
                error={<div className="error-message">Failed to load PDF</div>}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={containerWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            </div>
          )}

          {isDOC && (
            <div
              ref={docxContainerRef}
              className={`docx-preview ${zoomLevel === 1 ? 'zoom-fit' : ''}`}
              style={{ transform: zoomLevel === 1 ? 'none' : `scale(${zoomLevel})`, transformOrigin: 'top left' }}
            />
          )}

          {isImage && (
            <div className="image-preview">
              <img
                src={fileURL}
                alt={fileName || "Uploaded image preview"}
                className={`image-preview-img ${zoomLevel === 1 ? 'zoom-fit' : ''}`}
                style={{ transform: zoomLevel === 1 ? 'none' : `scale(${zoomLevel})`, transformOrigin: 'top left' }}
                onError={(e) => {
                  e.currentTarget.src = "/fallback-image.png";
                }}
              />
            </div>
          )}
          {!isSupported && <p>Unsupported file format</p>}
        </div>
      </div>
    </div>
  );
};